import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminApi, messagesApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import { Textarea } from '../components/ui/textarea';
import { 
    Select, 
    SelectContent, 
    SelectItem, 
    SelectTrigger, 
    SelectValue 
} from '../components/ui/select';
import { 
    Users, Search, FileText, Briefcase, ArrowLeft, Loader2, 
    ExternalLink, Github, Linkedin, Mail, ChevronLeft, ChevronRight,
    Shield, TrendingUp, Clock, Upload, MousePointer, Phone, Building,
    MessageSquare, Calendar, Send, RefreshCw, Eye, X
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

export default function AdminPage() {
    const navigate = useNavigate();
    const [isAdmin, setIsAdmin] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [talent, setTalent] = useState([]);
    const [stats, setStats] = useState(null);
    const [sectors, setSectors] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedSector, setSelectedSector] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalResults, setTotalResults] = useState(0);
    const [selectedTalent, setSelectedTalent] = useState(null);
    const [isSearching, setIsSearching] = useState(false);
    const [isLoadingDetails, setIsLoadingDetails] = useState(false);
    
    // JD Matching state
    const [activeTab, setActiveTab] = useState('talent'); // 'talent' or 'jd-match'
    const [jdTitle, setJdTitle] = useState('');
    const [jdText, setJdText] = useState('');
    const [jdMatches, setJdMatches] = useState([]);
    const [isMatchingJD, setIsMatchingJD] = useState(false);
    const [jdKeywords, setJdKeywords] = useState([]);
    const [isUploadingJD, setIsUploadingJD] = useState(false);
    const [jdFilename, setJdFilename] = useState('');
    
    // CV Preview state
    const [cvPreview, setCvPreview] = useState(null);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [lastRefresh, setLastRefresh] = useState(new Date());

    // Check admin access
    useEffect(() => {
        const checkAccess = async () => {
            try {
                const result = await adminApi.checkAdmin();
                if (!result.is_admin) {
                    toast.error('Admin access required');
                    navigate('/dashboard');
                    return;
                }
                setIsAdmin(true);
                loadInitialData();
            } catch (error) {
                toast.error('Access denied');
                navigate('/dashboard');
            } finally {
                setIsLoading(false);
            }
        };
        checkAccess();
    }, [navigate]);

    const loadInitialData = async () => {
        try {
            const [statsData, sectorsData] = await Promise.all([
                adminApi.getStats(),
                adminApi.getSectors()
            ]);
            setStats(statsData);
            setSectors(sectorsData.sectors || []);
            
            // Initial search
            searchTalent();
        } catch (error) {
            console.error('Error loading admin data:', error);
        }
    };

    const searchTalent = useCallback(async (page = 1) => {
        setIsSearching(true);
        try {
            const result = await adminApi.searchTalent({
                query: searchQuery || null,
                sector: selectedSector || null,
                page,
                limit: 20
            });
            setTalent(result.talent || []);
            setTotalPages(result.total_pages || 1);
            setTotalResults(result.total || 0);
            setCurrentPage(page);
        } catch (error) {
            toast.error('Search failed');
        } finally {
            setIsSearching(false);
        }
    }, [searchQuery, selectedSector]);

    const handleSearch = () => {
        setCurrentPage(1);
        searchTalent(1);
    };

    const handleRefresh = async () => {
        setIsRefreshing(true);
        try {
            await Promise.all([
                searchTalent(currentPage),
                loadInitialData()
            ]);
            setLastRefresh(new Date());
            toast.success('Data refreshed - showing latest CVs');
        } catch (error) {
            toast.error('Refresh failed');
        } finally {
            setIsRefreshing(false);
        }
    };

    const viewTalentDetails = async (cvId) => {
        setIsLoadingDetails(true);
        try {
            const result = await adminApi.getTalentDetails(cvId);
            setSelectedTalent(result);
        } catch (error) {
            toast.error('Failed to load details');
        } finally {
            setIsLoadingDetails(false);
        }
    };

    const handleMatchJD = async () => {
        if (!jdText || jdText.length < 50) {
            toast.error('Please enter a job description (at least 50 characters)');
            return;
        }
        
        setIsMatchingJD(true);
        setJdMatches([]);
        setJdKeywords([]);
        try {
            const result = await adminApi.matchJD(jdTitle || 'Job Position', jdText);
            setJdMatches(result.candidates || []);
            // Combine all detected info for display
            const detectedInfo = [];
            if (result.jd_role_type) detectedInfo.push(`Role: ${result.jd_role_type}`);
            if (result.jd_tech_skills?.length) detectedInfo.push(...result.jd_tech_skills);
            if (result.jd_domains?.length) detectedInfo.push(...result.jd_domains);
            setJdKeywords(detectedInfo);
            
            if (result.candidates?.length > 0) {
                toast.success(`Found ${result.candidates.length} matching ${result.jd_role_type || ''} candidates`);
            } else {
                toast.info(`No matching candidates found for ${result.jd_role_type || 'this'} role`);
            }
        } catch (error) {
            toast.error('Failed to match JD');
        } finally {
            setIsMatchingJD(false);
        }
    };

    const handleJDUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        
        const validTypes = ['.pdf', '.docx', '.doc', '.txt'];
        const fileExt = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
        if (!validTypes.includes(fileExt)) {
            toast.error('Please upload a PDF, DOCX, or TXT file');
            return;
        }
        
        setIsUploadingJD(true);
        try {
            const result = await adminApi.parseJD(file);
            setJdTitle(result.job_title || '');
            setJdText(result.jd_text || '');
            setJdFilename(result.filename || file.name);
            toast.success(`JD uploaded: ${result.job_title || file.name}`);
        } catch (error) {
            toast.error(error.message || 'Failed to parse JD file');
        } finally {
            setIsUploadingJD(false);
            e.target.value = ''; // Reset input
        }
    };

    if (isLoading) {
        return (
            <div className="min-h-screen bg-void flex items-center justify-center">
                <Loader2 className="w-8 h-8 text-acid animate-spin" />
            </div>
        );
    }

    if (!isAdmin) {
        return null;
    }

    return (
        <div className="min-h-screen bg-void page-enter">
            <div className="noise-overlay" />

            {/* Header */}
            <header className="sticky top-0 z-20 glass border-b border-border">
                <div className="max-w-7xl mx-auto px-6 py-4 flex items-center gap-4">
                    <Link 
                        to="/dashboard" 
                        className="text-muted-foreground hover:text-acid transition-colors"
                        data-testid="back-btn"
                    >
                        <ArrowLeft className="w-5 h-5" strokeWidth={1.5} />
                    </Link>
                    <div className="flex items-center gap-2">
                        <Shield className="w-5 h-5 text-acid" strokeWidth={1.5} />
                        <h1 className="font-display text-xl font-bold text-white">Admin Panel</h1>
                    </div>
                    <Badge className="ml-2 font-mono text-xs bg-acid/10 text-acid border-acid/30">
                        Talent Database
                    </Badge>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-6 py-8">
                {/* Stats Overview */}
                {stats && (
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                        <Card className="bg-void-paper border-border">
                            <CardContent className="p-4">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-acid/10 border border-acid/30 rounded-sm flex items-center justify-center">
                                        <Users className="w-5 h-5 text-acid" strokeWidth={1.5} />
                                    </div>
                                    <div>
                                        <div className="font-display text-2xl font-bold text-white">{stats.total_users}</div>
                                        <div className="font-mono text-xs uppercase tracking-wider text-muted-foreground">Total Users</div>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        <Card className="bg-void-paper border-border">
                            <CardContent className="p-4">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-cyber-purple/10 border border-cyber-purple/30 rounded-sm flex items-center justify-center">
                                        <FileText className="w-5 h-5 text-cyber-purple" strokeWidth={1.5} />
                                    </div>
                                    <div>
                                        <div className="font-display text-2xl font-bold text-white">{stats.total_cvs}</div>
                                        <div className="font-mono text-xs uppercase tracking-wider text-muted-foreground">Total CVs</div>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        <Card className="bg-void-paper border-border">
                            <CardContent className="p-4">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-neon-cyan/10 border border-neon-cyan/30 rounded-sm flex items-center justify-center">
                                        <Clock className="w-5 h-5 text-neon-cyan" strokeWidth={1.5} />
                                    </div>
                                    <div>
                                        <div className="font-display text-2xl font-bold text-white">{stats.recent_uploads}</div>
                                        <div className="font-mono text-xs uppercase tracking-wider text-muted-foreground">This Week</div>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        <Card className="bg-void-paper border-border">
                            <CardContent className="p-4">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-neon-red/10 border border-neon-red/30 rounded-sm flex items-center justify-center">
                                        <TrendingUp className="w-5 h-5 text-neon-red" strokeWidth={1.5} />
                                    </div>
                                    <div>
                                        <div className="font-display text-2xl font-bold text-white">{Object.keys(stats.cvs_by_sector || {}).length}</div>
                                        <div className="font-mono text-xs uppercase tracking-wider text-muted-foreground">Sectors</div>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                )}

                {/* Tab Navigation */}
                <div className="flex gap-2 mb-6">
                    <Button
                        variant={activeTab === 'talent' ? 'default' : 'outline'}
                        onClick={() => setActiveTab('talent')}
                        className={activeTab === 'talent' 
                            ? 'bg-acid text-black font-mono uppercase tracking-wider text-sm' 
                            : 'border-border text-muted-foreground hover:border-acid hover:text-acid font-mono uppercase tracking-wider text-sm'
                        }
                        data-testid="tab-talent"
                    >
                        <Users className="w-4 h-4 mr-2" strokeWidth={1.5} />
                        Search Talent
                    </Button>
                    <Button
                        variant={activeTab === 'jd-match' ? 'default' : 'outline'}
                        onClick={() => setActiveTab('jd-match')}
                        className={activeTab === 'jd-match' 
                            ? 'bg-acid text-black font-mono uppercase tracking-wider text-sm' 
                            : 'border-border text-muted-foreground hover:border-acid hover:text-acid font-mono uppercase tracking-wider text-sm'
                        }
                        data-testid="tab-jd-match"
                    >
                        <Briefcase className="w-4 h-4 mr-2" strokeWidth={1.5} />
                        Match JD to Candidates
                    </Button>
                </div>

                {/* Tab Content */}
                {activeTab === 'talent' ? (
                <div className="grid lg:grid-cols-3 gap-6">
                    {/* Search & Results */}
                    <div className="lg:col-span-2 space-y-6">
                        {/* Search Card */}
                        <Card className="bg-void-paper border-border">
                            <CardHeader className="pb-3">
                                <CardTitle className="font-display text-lg flex items-center gap-2">
                                    <Search className="w-5 h-5 text-acid" strokeWidth={1.5} />
                                    Search Talent
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="flex flex-col sm:flex-row gap-3">
                                    <Input
                                        placeholder="Search by name, skills, keywords..."
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        className="flex-1 bg-black/20 border-border focus:border-acid font-mono text-sm"
                                        onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                                        data-testid="talent-search-input"
                                    />
                                    <Select value={selectedSector || "all"} onValueChange={(val) => setSelectedSector(val === "all" ? "" : val)}>
                                        <SelectTrigger className="w-full sm:w-[180px] bg-black/20 border-border">
                                            <SelectValue placeholder="All Sectors" />
                                        </SelectTrigger>
                                        <SelectContent className="bg-void-paper border-border">
                                            <SelectItem value="all">All Sectors</SelectItem>
                                            {sectors.map((sector) => (
                                                <SelectItem key={sector} value={sector}>
                                                    {sector.charAt(0).toUpperCase() + sector.slice(1)}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    <Button
                                        onClick={handleSearch}
                                        disabled={isSearching}
                                        className="bg-acid text-black font-mono uppercase tracking-wider text-sm px-6 hover:bg-acid-hover"
                                        data-testid="search-btn"
                                    >
                                        {isSearching ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Search'}
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>

                        {/* Results */}
                        <Card className="bg-void-paper border-border">
                            <CardHeader className="pb-3">
                                <div className="flex items-center justify-between">
                                    <CardTitle className="font-display text-lg flex items-center gap-2">
                                        <Users className="w-5 h-5 text-cyber-purple" strokeWidth={1.5} />
                                        Talent Pool
                                    </CardTitle>
                                    <div className="flex items-center gap-2">
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={handleRefresh}
                                            disabled={isRefreshing}
                                            className="text-muted-foreground hover:text-acid"
                                            title={`Last refresh: ${lastRefresh.toLocaleTimeString()}`}
                                            data-testid="refresh-btn"
                                        >
                                            <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} strokeWidth={1.5} />
                                        </Button>
                                        <Badge className="font-mono text-xs bg-cyber-purple/10 text-cyber-purple border-cyber-purple/30">
                                            {totalResults} results
                                        </Badge>
                                    </div>
                                </div>
                            </CardHeader>
                            <CardContent>
                                {isSearching ? (
                                    <div className="text-center py-12">
                                        <Loader2 className="w-8 h-8 text-acid animate-spin mx-auto mb-3" />
                                        <p className="text-muted-foreground text-sm">Searching talent...</p>
                                    </div>
                                ) : talent.length === 0 ? (
                                    <div className="text-center py-12">
                                        <Users className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" strokeWidth={1.5} />
                                        <p className="text-muted-foreground text-sm">No talent found</p>
                                    </div>
                                ) : (
                                    <>
                                        <ScrollArea className="h-[500px]">
                                            <div className="space-y-3">
                                                {talent.map((item) => (
                                                    <TalentCard 
                                                        key={item.cv_id} 
                                                        talent={item} 
                                                        onView={() => viewTalentDetails(item.cv_id)}
                                                        onPreview={() => setCvPreview(item)}
                                                        isSelected={selectedTalent?.cv?.cv_id === item.cv_id}
                                                    />
                                                ))}
                                            </div>
                                        </ScrollArea>
                                        
                                        {/* Pagination */}
                                        {totalPages > 1 && (
                                            <div className="flex items-center justify-center gap-2 mt-4 pt-4 border-t border-border">
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => searchTalent(currentPage - 1)}
                                                    disabled={currentPage === 1}
                                                    className="border-border"
                                                >
                                                    <ChevronLeft className="w-4 h-4" />
                                                </Button>
                                                <span className="font-mono text-sm text-muted-foreground">
                                                    Page {currentPage} of {totalPages}
                                                </span>
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => searchTalent(currentPage + 1)}
                                                    disabled={currentPage === totalPages}
                                                    className="border-border"
                                                >
                                                    <ChevronRight className="w-4 h-4" />
                                                </Button>
                                            </div>
                                        )}
                                    </>
                                )}
                            </CardContent>
                        </Card>
                    </div>

                    {/* Detail Panel */}
                    <div className="space-y-6">
                        {isLoadingDetails ? (
                            <Card className="bg-void-paper border-border">
                                <CardContent className="text-center py-16">
                                    <Loader2 className="w-8 h-8 text-acid animate-spin mx-auto mb-3" />
                                    <p className="text-muted-foreground text-sm">Loading details...</p>
                                </CardContent>
                            </Card>
                        ) : selectedTalent ? (
                            <TalentDetailPanel talent={selectedTalent} onClose={() => setSelectedTalent(null)} />
                        ) : (
                            <Card className="bg-void-paper border-border">
                                <CardContent className="text-center py-16">
                                    <FileText className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" strokeWidth={1.5} />
                                    <p className="text-muted-foreground text-sm">
                                        Select a talent to view details
                                    </p>
                                </CardContent>
                            </Card>
                        )}

                        {/* Sector Breakdown */}
                        {stats?.cvs_by_sector && (
                            <Card className="bg-void-paper border-border">
                                <CardHeader className="pb-3">
                                    <CardTitle className="font-display text-sm">CVs by Sector</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="space-y-2">
                                        {Object.entries(stats.cvs_by_sector).map(([sector, count]) => (
                                            <div key={sector} className="flex items-center justify-between">
                                                <span className="text-sm text-muted-foreground capitalize">{sector || 'Unknown'}</span>
                                                <Badge variant="outline" className="font-mono text-xs">
                                                    {count}
                                                </Badge>
                                            </div>
                                        ))}
                                    </div>
                                </CardContent>
                            </Card>
                        )}
                    </div>
                </div>
                ) : (
                /* JD Matching Tab */
                <div className="grid lg:grid-cols-2 gap-6">
                    {/* JD Input */}
                    <Card className="bg-void-paper border-border">
                        <CardHeader className="pb-3">
                            <CardTitle className="font-display text-lg flex items-center gap-2">
                                <Briefcase className="w-5 h-5 text-acid" strokeWidth={1.5} />
                                Job Description
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            {/* Upload JD */}
                            <div className="border-2 border-dashed border-border rounded-sm p-4 hover:border-acid/50 transition-colors">
                                <input
                                    type="file"
                                    accept=".pdf,.docx,.doc,.txt"
                                    onChange={handleJDUpload}
                                    className="hidden"
                                    id="jd-upload"
                                    disabled={isUploadingJD}
                                />
                                <label 
                                    htmlFor="jd-upload" 
                                    className="flex flex-col items-center gap-2 cursor-pointer"
                                >
                                    {isUploadingJD ? (
                                        <Loader2 className="w-8 h-8 text-acid animate-spin" />
                                    ) : (
                                        <Upload className="w-8 h-8 text-muted-foreground" strokeWidth={1.5} />
                                    )}
                                    <span className="text-sm text-muted-foreground">
                                        {isUploadingJD ? 'Parsing JD...' : 'Upload JD (PDF, DOCX, TXT)'}
                                    </span>
                                    {jdFilename && (
                                        <Badge className="font-mono text-xs bg-acid/10 text-acid border-acid/30">
                                            {jdFilename}
                                        </Badge>
                                    )}
                                </label>
                            </div>

                            <div className="relative">
                                <div className="absolute inset-0 flex items-center">
                                    <span className="w-full border-t border-border" />
                                </div>
                                <div className="relative flex justify-center text-xs uppercase">
                                    <span className="bg-void-paper px-2 text-muted-foreground font-mono">
                                        or enter manually
                                    </span>
                                </div>
                            </div>

                            <div>
                                <label className="text-muted-foreground text-xs font-mono uppercase tracking-wider mb-2 block">
                                    Job Title
                                </label>
                                <Input
                                    placeholder="e.g., Senior Product Manager"
                                    value={jdTitle}
                                    onChange={(e) => setJdTitle(e.target.value)}
                                    className="bg-black/20 border-border focus:border-acid font-mono text-sm"
                                    data-testid="jd-title-input"
                                />
                            </div>
                            <div>
                                <label className="text-muted-foreground text-xs font-mono uppercase tracking-wider mb-2 block">
                                    Job Description
                                </label>
                                <textarea
                                    placeholder="Paste the full job description here..."
                                    value={jdText}
                                    onChange={(e) => setJdText(e.target.value)}
                                    className="w-full h-[250px] p-3 bg-black/20 border border-border rounded-sm focus:border-acid focus:outline-none font-mono text-sm text-white resize-none"
                                    data-testid="jd-text-input"
                                />
                            </div>
                            <Button
                                onClick={handleMatchJD}
                                disabled={isMatchingJD || jdText.length < 50}
                                className="w-full bg-acid text-black font-mono uppercase tracking-wider text-sm hover:bg-acid-hover"
                                data-testid="match-jd-btn"
                            >
                                {isMatchingJD ? (
                                    <>
                                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                                        Matching...
                                    </>
                                ) : (
                                    <>
                                        <Search className="w-4 h-4 mr-2" strokeWidth={1.5} />
                                        Find Matching Candidates
                                    </>
                                )}
                            </Button>
                            
                            {jdKeywords.length > 0 && (
                                <div className="pt-3 border-t border-border">
                                    <p className="text-muted-foreground text-xs font-mono uppercase tracking-wider mb-2">
                                        Keywords Detected:
                                    </p>
                                    <div className="flex flex-wrap gap-1">
                                        {jdKeywords.map((kw, idx) => (
                                            <Badge 
                                                key={idx}
                                                variant="outline"
                                                className="font-mono text-xs border-neon-cyan/30 text-neon-cyan bg-neon-cyan/5"
                                            >
                                                {kw}
                                            </Badge>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    {/* Matching Candidates */}
                    <Card className="bg-void-paper border-border">
                        <CardHeader className="pb-3">
                            <div className="flex items-center justify-between">
                                <CardTitle className="font-display text-lg flex items-center gap-2">
                                    <Users className="w-5 h-5 text-neon-cyan" strokeWidth={1.5} />
                                    Matching Candidates
                                </CardTitle>
                                {jdMatches.length > 0 && (
                                    <Badge className="font-mono text-xs bg-acid/10 text-acid border-acid/30">
                                        {jdMatches.length} found
                                    </Badge>
                                )}
                            </div>
                        </CardHeader>
                        <CardContent>
                            {isMatchingJD ? (
                                <div className="text-center py-12">
                                    <Loader2 className="w-8 h-8 text-acid animate-spin mx-auto mb-3" />
                                    <p className="text-muted-foreground text-sm">Analyzing candidates...</p>
                                </div>
                            ) : jdMatches.length === 0 ? (
                                <div className="text-center py-12">
                                    <FileText className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" strokeWidth={1.5} />
                                    <p className="text-muted-foreground text-sm">
                                        {jdText ? 'No matching candidates found' : 'Enter a job description to find matching candidates'}
                                    </p>
                                </div>
                            ) : (
                                <ScrollArea className="h-[500px]">
                                    <div className="space-y-3">
                                        {jdMatches.map((candidate, idx) => (
                                            <CandidateMatchCard key={candidate.cv_id || idx} candidate={candidate} />
                                        ))}
                                    </div>
                                </ScrollArea>
                            )}
                        </CardContent>
                    </Card>
                </div>
                )}
            </main>

            {/* CV Preview Modal */}
            {cvPreview && (
                <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
                    <Card className="bg-void-paper border-border w-full max-w-3xl max-h-[90vh] flex flex-col">
                        <CardHeader className="pb-3 border-b border-border flex-shrink-0">
                            <div className="flex items-center justify-between">
                                <CardTitle className="font-display text-lg flex items-center gap-2">
                                    <FileText className="w-5 h-5 text-acid" strokeWidth={1.5} />
                                    CV Preview: {cvPreview.user?.name || cvPreview.candidate_name || 'Unknown'}
                                </CardTitle>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setCvPreview(null)}
                                    className="text-muted-foreground hover:text-white"
                                >
                                    <X className="w-5 h-5" strokeWidth={1.5} />
                                </Button>
                            </div>
                            <div className="flex items-center gap-2 mt-2">
                                {cvPreview.primary_sector && (
                                    <Badge className="font-mono text-xs bg-cyber-purple/10 text-cyber-purple border-cyber-purple/30 capitalize">
                                        {cvPreview.primary_sector}
                                    </Badge>
                                )}
                                {cvPreview.source === 'telegram' && (
                                    <Badge className="font-mono text-xs bg-[#0088cc]/10 text-[#0088cc] border-[#0088cc]/30">
                                        Via Telegram
                                    </Badge>
                                )}
                                <span className="text-xs text-muted-foreground ml-auto">
                                    {cvPreview.filename}
                                </span>
                            </div>
                        </CardHeader>
                        <CardContent className="p-4 overflow-y-auto flex-1">
                            {/* Skills */}
                            {cvPreview.skills?.length > 0 && (
                                <div className="mb-4">
                                    <h4 className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-2">Skills</h4>
                                    <div className="flex flex-wrap gap-1">
                                        {cvPreview.skills.map((skill, idx) => (
                                            <Badge key={idx} variant="outline" className="text-xs border-acid/30 text-acid">
                                                {skill}
                                            </Badge>
                                        ))}
                                    </div>
                                </div>
                            )}
                            
                            {/* Experience */}
                            {cvPreview.experience?.length > 0 && (
                                <div className="mb-4">
                                    <h4 className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-2">Experience</h4>
                                    <ul className="space-y-2">
                                        {cvPreview.experience.slice(0, 5).map((exp, idx) => (
                                            <li key={idx} className="text-sm text-white/90 pl-3 border-l-2 border-acid/30">
                                                {exp}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                            
                            {/* Education */}
                            {cvPreview.education?.length > 0 && (
                                <div className="mb-4">
                                    <h4 className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-2">Education</h4>
                                    <ul className="space-y-1">
                                        {cvPreview.education.map((edu, idx) => (
                                            <li key={idx} className="text-sm text-white/80">
                                                {edu}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                            
                            {/* Summary */}
                            {cvPreview.summary && (
                                <div className="mb-4">
                                    <h4 className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-2">Summary</h4>
                                    <p className="text-sm text-white/80 leading-relaxed">
                                        {cvPreview.summary}
                                    </p>
                                </div>
                            )}
                            
                            {/* Full CV Text Preview */}
                            {cvPreview.parsed_text && (
                                <div>
                                    <h4 className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-2">Full CV Content</h4>
                                    <div className="bg-black/30 rounded-sm p-4 border border-border max-h-[300px] overflow-y-auto">
                                        <pre className="text-xs text-white/70 whitespace-pre-wrap font-mono">
                                            {cvPreview.parsed_text}
                                        </pre>
                                    </div>
                                </div>
                            )}
                            
                            {/* Contact Info */}
                            {(cvPreview.user?.email || cvPreview.user?.telegram_handle) && (
                                <div className="mt-4 pt-4 border-t border-border">
                                    <h4 className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-2">Contact</h4>
                                    <div className="flex flex-wrap gap-3">
                                        {cvPreview.user?.email && (
                                            <a href={`mailto:${cvPreview.user.email}`} className="flex items-center gap-1 text-sm text-acid hover:underline">
                                                <Mail className="w-4 h-4" /> {cvPreview.user.email}
                                            </a>
                                        )}
                                        {cvPreview.user?.telegram_handle && (
                                            <a href={`https://t.me/${cvPreview.user.telegram_handle}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-sm text-[#0088cc] hover:underline">
                                                <Send className="w-4 h-4" /> @{cvPreview.user.telegram_handle}
                                            </a>
                                        )}
                                    </div>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </div>
            )}
        </div>
    );
}

function TalentCard({ talent, onView, onPreview, isSelected }) {
    return (
        <div 
            className={`p-4 bg-void-subtle border rounded-sm cursor-pointer transition-all group ${
                isSelected ? 'border-acid bg-acid/5' : 'border-border hover:border-acid/50'
            }`}
            onClick={onView}
            data-testid={`talent-card-${talent.cv_id}`}
        >
            <div className="flex items-start gap-3">
                <Avatar className="w-10 h-10 border border-border">
                    <AvatarImage src={talent.user?.picture} />
                    <AvatarFallback className="bg-void-paper text-muted-foreground font-display">
                        {talent.user?.name?.charAt(0) || '?'}
                    </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-display text-sm font-bold text-white truncate group-hover:text-acid transition-colors">
                            {talent.user?.name || 'Unknown'}
                        </h3>
                        {talent.primary_sector && (
                            <Badge className="font-mono text-[10px] bg-cyber-purple/10 text-cyber-purple border-cyber-purple/30 capitalize">
                                {talent.primary_sector}
                            </Badge>
                        )}
                        {talent.source === 'telegram' && (
                            <Badge className="font-mono text-[10px] bg-[#0088cc]/10 text-[#0088cc] border-[#0088cc]/30">
                                TG
                            </Badge>
                        )}
                    </div>
                    <p className="text-muted-foreground text-xs truncate mb-2">
                        {talent.filename}
                    </p>
                    <div className="flex items-center justify-between">
                        <div className="flex flex-wrap gap-1">
                            {talent.skills?.slice(0, 4).map((skill, idx) => (
                                <Badge 
                                    key={idx}
                                    variant="outline"
                                    className="font-mono text-[10px] border-border text-muted-foreground"
                                >
                                    {skill}
                                </Badge>
                            ))}
                            {talent.skills?.length > 4 && (
                                <Badge variant="outline" className="font-mono text-[10px] border-border text-muted-foreground">
                                    +{talent.skills.length - 4}
                                </Badge>
                            )}
                        </div>
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => { e.stopPropagation(); onPreview?.(); }}
                            className="text-muted-foreground hover:text-neon-cyan opacity-0 group-hover:opacity-100 transition-opacity"
                            title="Preview CV"
                        >
                            <Eye className="w-4 h-4" strokeWidth={1.5} />
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
}

function TalentDetailPanel({ talent, onClose }) {
    const { cv, user } = talent;
    const [activeDetailTab, setActiveDetailTab] = useState('profile'); // 'profile', 'activity', or 'contact'
    const [clickedJobs, setClickedJobs] = useState([]);
    const [isLoadingClicks, setIsLoadingClicks] = useState(false);
    const [contacts, setContacts] = useState({});
    const [loadingContacts, setLoadingContacts] = useState({});
    const [messageContent, setMessageContent] = useState('');
    const [proposedMeetingTime, setProposedMeetingTime] = useState('');
    const [isSendingMessage, setIsSendingMessage] = useState(false);

    // Load clicked jobs when switching to activity tab
    useEffect(() => {
        if (activeDetailTab === 'activity' && user?.user_id && clickedJobs.length === 0) {
            loadClickedJobs();
        }
    }, [activeDetailTab, user?.user_id]);

    const loadClickedJobs = async () => {
        if (!user?.user_id) return;
        setIsLoadingClicks(true);
        try {
            const result = await adminApi.getUserClickedJobs(user.user_id);
            setClickedJobs(result.clicked_jobs || []);
        } catch (error) {
            console.error('Failed to load clicked jobs:', error);
        } finally {
            setIsLoadingClicks(false);
        }
    };

    const loadCompanyContacts = async (companyName) => {
        if (!companyName || contacts[companyName] || loadingContacts[companyName]) return;
        
        setLoadingContacts(prev => ({ ...prev, [companyName]: true }));
        try {
            const result = await adminApi.getCompanyContacts(companyName);
            setContacts(prev => ({ ...prev, [companyName]: result.contacts || [] }));
        } catch (error) {
            console.error('Failed to load contacts:', error);
            toast.error(`Failed to load contacts for ${companyName}`);
        } finally {
            setLoadingContacts(prev => ({ ...prev, [companyName]: false }));
        }
    };

    const handleSendMessage = async () => {
        if (!messageContent.trim()) {
            toast.error('Please enter a message');
            return;
        }
        
        setIsSendingMessage(true);
        try {
            await messagesApi.sendMessage({
                recipient_id: user.user_id,
                content: messageContent.trim()
            });
            toast.success('Message sent to candidate');
            setMessageContent('');
        } catch (error) {
            toast.error('Failed to send message');
        } finally {
            setIsSendingMessage(false);
        }
    };

    const handleRequestMeeting = async () => {
        if (!messageContent.trim()) {
            toast.error('Please enter a message for the meeting request');
            return;
        }
        
        setIsSendingMessage(true);
        try {
            await messagesApi.requestMeeting(user.user_id, messageContent.trim(), proposedMeetingTime);
            toast.success('Meeting request sent to candidate');
            setMessageContent('');
            setProposedMeetingTime('');
        } catch (error) {
            toast.error('Failed to send meeting request');
        } finally {
            setIsSendingMessage(false);
        }
    };
    
    return (
        <Card className="bg-void-paper border-border">
            <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                    <CardTitle className="font-display text-lg">Talent Details</CardTitle>
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={onClose}
                        className="text-muted-foreground hover:text-white"
                    >
                        ×
                    </Button>
                </div>
                {/* Inner Tab Navigation */}
                <div className="flex gap-2 mt-3">
                    <Button
                        variant={activeDetailTab === 'profile' ? 'default' : 'ghost'}
                        size="sm"
                        onClick={() => setActiveDetailTab('profile')}
                        className={activeDetailTab === 'profile' 
                            ? 'bg-acid text-black font-mono text-xs' 
                            : 'text-muted-foreground font-mono text-xs'
                        }
                    >
                        <FileText className="w-3 h-3 mr-1" /> Profile
                    </Button>
                    <Button
                        variant={activeDetailTab === 'activity' ? 'default' : 'ghost'}
                        size="sm"
                        onClick={() => setActiveDetailTab('activity')}
                        className={activeDetailTab === 'activity' 
                            ? 'bg-acid text-black font-mono text-xs' 
                            : 'text-muted-foreground font-mono text-xs'
                        }
                    >
                        <MousePointer className="w-3 h-3 mr-1" /> Activity
                    </Button>
                    <Button
                        variant={activeDetailTab === 'contact' ? 'default' : 'ghost'}
                        size="sm"
                        onClick={() => setActiveDetailTab('contact')}
                        className={activeDetailTab === 'contact' 
                            ? 'bg-acid text-black font-mono text-xs' 
                            : 'text-muted-foreground font-mono text-xs'
                        }
                    >
                        <MessageSquare className="w-3 h-3 mr-1" /> Contact
                    </Button>
                </div>
            </CardHeader>
            <CardContent>
                <ScrollArea className="h-[600px]">
                    {/* User Info Header - Always visible */}
                    <div className="flex items-center gap-4 mb-6 pb-4 border-b border-border">
                        <Avatar className="w-16 h-16 border-2 border-acid">
                            <AvatarImage src={user?.picture} />
                            <AvatarFallback className="bg-acid text-black font-display text-xl">
                                {user?.name?.charAt(0) || '?'}
                            </AvatarFallback>
                        </Avatar>
                        <div>
                            <h3 className="font-display text-lg font-bold text-white">{user?.name}</h3>
                            <p className="text-muted-foreground text-sm flex items-center gap-1">
                                <Mail className="w-3 h-3" /> {user?.email}
                            </p>
                            <div className="flex gap-2 mt-2">
                                {user?.github_url && (
                                    <a href={user.github_url} target="_blank" rel="noopener noreferrer" 
                                       className="text-muted-foreground hover:text-acid">
                                        <Github className="w-4 h-4" strokeWidth={1.5} />
                                    </a>
                                )}
                                {user?.linkedin_url && (
                                    <a href={user.linkedin_url} target="_blank" rel="noopener noreferrer"
                                       className="text-muted-foreground hover:text-acid">
                                        <Linkedin className="w-4 h-4" strokeWidth={1.5} />
                                    </a>
                                )}
                            </div>
                        </div>
                    </div>

                    {activeDetailTab === 'profile' ? (
                        /* Profile Tab Content */
                        <div className="space-y-4">
                            <div>
                                <h4 className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-2">Sector</h4>
                                <Badge className="font-mono text-xs bg-cyber-purple/10 text-cyber-purple border-cyber-purple/30 capitalize">
                                    {cv?.primary_sector || 'Unknown'}
                                </Badge>
                            </div>

                            {cv?.summary && (
                                <div>
                                    <h4 className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-2">Summary</h4>
                                    <p className="text-sm text-muted-foreground leading-relaxed">{cv.summary}</p>
                                </div>
                            )}

                            {cv?.skills?.length > 0 && (
                                <div>
                                    <h4 className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-2">Skills</h4>
                                    <div className="flex flex-wrap gap-1">
                                        {cv.skills.map((skill, idx) => (
                                            <Badge 
                                                key={idx}
                                                variant="outline"
                                                className="font-mono text-xs border-acid/30 text-acid bg-acid/5"
                                            >
                                                {skill}
                                            </Badge>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {cv?.sector_keywords?.length > 0 && (
                                <div>
                                    <h4 className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-2">Sector Keywords</h4>
                                    <div className="flex flex-wrap gap-1">
                                        {cv.sector_keywords.map((kw, idx) => (
                                            <Badge 
                                                key={idx}
                                                variant="outline"
                                                className="font-mono text-xs border-neon-cyan/30 text-neon-cyan bg-neon-cyan/5"
                                            >
                                                {kw}
                                            </Badge>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {cv?.experience?.length > 0 && (
                                <div>
                                    <h4 className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-2">Experience</h4>
                                    <div className="space-y-2">
                                        {cv.experience.map((exp, idx) => (
                                            <div key={idx} className="p-2 bg-black/20 rounded-sm">
                                                <p className="text-sm text-white">{exp}</p>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {cv?.education?.length > 0 && (
                                <div>
                                    <h4 className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-2">Education</h4>
                                    <div className="space-y-1">
                                        {cv.education.map((edu, idx) => (
                                            <p key={idx} className="text-sm text-muted-foreground">{edu}</p>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div className="pt-4 border-t border-border">
                                <p className="text-xs text-muted-foreground font-mono">
                                    CV: {cv?.filename}<br />
                                    Uploaded: {new Date(cv?.uploaded_at).toLocaleDateString()}
                                </p>
                            </div>
                        </div>
                    ) : (
                        /* Activity Tab Content - Clicked Jobs & Apollo Contacts */
                        <div className="space-y-4">
                            <div>
                                <h4 className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-3 flex items-center gap-2">
                                    <MousePointer className="w-3 h-3" />
                                    Jobs Clicked ({clickedJobs.length})
                                </h4>
                                
                                {isLoadingClicks ? (
                                    <div className="flex items-center justify-center py-8">
                                        <Loader2 className="w-5 h-5 text-acid animate-spin" />
                                    </div>
                                ) : clickedJobs.length === 0 ? (
                                    <p className="text-muted-foreground text-sm text-center py-4">
                                        No job clicks tracked yet
                                    </p>
                                ) : (
                                    <div className="space-y-4">
                                        {clickedJobs.map((click, idx) => (
                                            <div key={click.click_id || idx} className="p-3 bg-black/20 rounded-sm border border-border">
                                                <div className="flex items-start justify-between gap-2 mb-2">
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-sm font-medium text-white truncate">
                                                            {click.job_title || 'Job Position'}
                                                        </p>
                                                        <p className="text-acid text-sm font-semibold flex items-center gap-1">
                                                            <Building className="w-3 h-3" />
                                                            {click.company || 'Unknown Company'}
                                                        </p>
                                                        <p className="text-muted-foreground text-xs font-mono mt-1">
                                                            Clicked: {new Date(click.clicked_at).toLocaleString()}
                                                        </p>
                                                    </div>
                                                    <a
                                                        href={click.job_url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="text-muted-foreground hover:text-neon-cyan"
                                                    >
                                                        <ExternalLink className="w-4 h-4" strokeWidth={1.5} />
                                                    </a>
                                                </div>
                                                
                                                {/* Apollo Contacts Section */}
                                                {click.company && (
                                                    <div className="mt-3 pt-3 border-t border-border/50">
                                                        <div className="flex items-center justify-between mb-2">
                                                            <p className="text-xs font-mono text-muted-foreground uppercase tracking-wider">
                                                                Contacts at {click.company}
                                                            </p>
                                                            {!contacts[click.company] && (
                                                                <Button
                                                                    variant="ghost"
                                                                    size="sm"
                                                                    onClick={() => loadCompanyContacts(click.company)}
                                                                    disabled={loadingContacts[click.company]}
                                                                    className="text-xs text-acid hover:bg-acid/10 h-6"
                                                                    data-testid={`load-contacts-${idx}`}
                                                                >
                                                                    {loadingContacts[click.company] ? (
                                                                        <Loader2 className="w-3 h-3 animate-spin mr-1" />
                                                                    ) : (
                                                                        <Users className="w-3 h-3 mr-1" />
                                                                    )}
                                                                    Find Contacts
                                                                </Button>
                                                            )}
                                                        </div>
                                                        
                                                        {contacts[click.company] && (
                                                            <div className="space-y-2">
                                                                {contacts[click.company].length === 0 ? (
                                                                    <p className="text-muted-foreground text-xs">
                                                                        No contacts found
                                                                    </p>
                                                                ) : (
                                                                    contacts[click.company].map((contact, cIdx) => (
                                                                        <div 
                                                                            key={contact.id || cIdx}
                                                                            className="p-2 bg-void-subtle rounded-sm border border-border/30"
                                                                        >
                                                                            <div className="flex items-start justify-between">
                                                                                <div>
                                                                                    <p className="text-white text-sm font-medium">
                                                                                        {contact.first_name} {contact.last_name}
                                                                                    </p>
                                                                                    <p className="text-cyber-purple text-xs">
                                                                                        {contact.title}
                                                                                    </p>
                                                                                </div>
                                                                                <div className="flex gap-2">
                                                                                    {contact.email && (
                                                                                        <a
                                                                                            href={`mailto:${contact.email}`}
                                                                                            className="text-muted-foreground hover:text-acid"
                                                                                            title={contact.email}
                                                                                        >
                                                                                            <Mail className="w-3 h-3" />
                                                                                        </a>
                                                                                    )}
                                                                                    {contact.phone && (
                                                                                        <a
                                                                                            href={`tel:${contact.phone}`}
                                                                                            className="text-muted-foreground hover:text-acid"
                                                                                            title={contact.phone}
                                                                                        >
                                                                                            <Phone className="w-3 h-3" />
                                                                                        </a>
                                                                                    )}
                                                                                    {contact.linkedin_url && (
                                                                                        <a
                                                                                            href={contact.linkedin_url}
                                                                                            target="_blank"
                                                                                            rel="noopener noreferrer"
                                                                                            className="text-muted-foreground hover:text-acid"
                                                                                        >
                                                                                            <Linkedin className="w-3 h-3" />
                                                                                        </a>
                                                                                    )}
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    ))
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Contact Tab Content */}
                    {activeDetailTab === 'contact' && (
                        <div className="space-y-4">
                            <div>
                                <h4 className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-3 flex items-center gap-2">
                                    <MessageSquare className="w-3 h-3" />
                                    Send Message to {user?.name}
                                </h4>
                                
                                <div className="space-y-4">
                                    {/* Message textarea */}
                                    <div>
                                        <label className="text-muted-foreground text-xs font-mono mb-2 block">
                                            Your Message
                                        </label>
                                        <Textarea
                                            placeholder="Hi, I noticed your profile and would like to discuss an opportunity..."
                                            value={messageContent}
                                            onChange={(e) => setMessageContent(e.target.value)}
                                            className="bg-black/20 border-border focus:border-acid font-mono text-sm min-h-[120px]"
                                            data-testid="admin-message-textarea"
                                        />
                                    </div>

                                    {/* Send Message Button */}
                                    <Button
                                        onClick={handleSendMessage}
                                        disabled={isSendingMessage || !messageContent.trim()}
                                        className="w-full bg-acid text-black hover:bg-acid-hover font-mono uppercase tracking-wider"
                                        data-testid="send-candidate-message-btn"
                                    >
                                        {isSendingMessage ? (
                                            <Loader2 className="w-4 h-4 animate-spin mr-2" />
                                        ) : (
                                            <Send className="w-4 h-4 mr-2" />
                                        )}
                                        Send Message
                                    </Button>

                                    {/* Divider */}
                                    <div className="flex items-center gap-4 py-2">
                                        <div className="flex-1 border-t border-border/50"></div>
                                        <span className="text-muted-foreground/50 text-xs font-mono">OR</span>
                                        <div className="flex-1 border-t border-border/50"></div>
                                    </div>

                                    {/* Meeting Request Section */}
                                    <div className="p-4 border border-cyber-purple/30 bg-cyber-purple/5 rounded-sm">
                                        <h5 className="font-mono text-xs uppercase tracking-wider text-cyber-purple mb-3 flex items-center gap-2">
                                            <Calendar className="w-3 h-3" />
                                            Request a Meeting
                                        </h5>
                                        
                                        <div className="space-y-3">
                                            <div>
                                                <label className="text-muted-foreground text-xs font-mono mb-2 block">
                                                    Proposed Time (optional)
                                                </label>
                                                <Input
                                                    type="text"
                                                    placeholder="e.g., Next Tuesday at 3pm UTC"
                                                    value={proposedMeetingTime}
                                                    onChange={(e) => setProposedMeetingTime(e.target.value)}
                                                    className="bg-black/20 border-border focus:border-cyber-purple font-mono text-sm"
                                                    data-testid="proposed-meeting-time"
                                                />
                                            </div>

                                            <Button
                                                onClick={handleRequestMeeting}
                                                disabled={isSendingMessage || !messageContent.trim()}
                                                variant="outline"
                                                className="w-full border-cyber-purple text-cyber-purple hover:bg-cyber-purple/10 font-mono uppercase tracking-wider"
                                                data-testid="request-meeting-btn"
                                            >
                                                {isSendingMessage ? (
                                                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                                                ) : (
                                                    <Calendar className="w-4 h-4 mr-2" />
                                                )}
                                                Request Meeting
                                            </Button>
                                        </div>
                                    </div>

                                    {/* Info note */}
                                    <p className="text-muted-foreground/50 text-xs text-center">
                                        The candidate will receive your message in their Messages inbox
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}
                </ScrollArea>
            </CardContent>
        </Card>
    );
}

function CandidateMatchCard({ candidate }) {
    const getFitLabelStyle = (label) => {
        if (label?.includes('strong')) return 'text-acid border-acid/30 bg-acid/10';
        if (label?.includes('good')) return 'text-neon-cyan border-neon-cyan/30 bg-neon-cyan/10';
        return 'text-amber-400 border-amber-400/30 bg-amber-400/10';
    };

    const getFitBorderColor = (label) => {
        if (label?.includes('strong')) return 'border-l-acid';
        if (label?.includes('good')) return 'border-l-neon-cyan';
        return 'border-l-amber-400';
    };

    return (
        <div className={`p-4 bg-void-subtle border border-border border-l-4 ${getFitBorderColor(candidate.fit_label)} rounded-sm`}>
            {/* Header */}
            <div className="flex items-start gap-3 mb-3">
                <Avatar className="w-10 h-10 border border-acid/30">
                    <AvatarImage src={candidate.user?.picture} />
                    <AvatarFallback className="bg-acid/20 text-acid font-display text-sm">
                        {(candidate.candidate_name || candidate.user?.name)?.charAt(0) || '?'}
                    </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                    <h4 className="font-display font-bold text-white truncate text-lg">
                        {candidate.candidate_name || candidate.user?.name || 'Unknown'}
                    </h4>
                    {candidate.candidate_name && candidate.user?.name && candidate.candidate_name !== candidate.user?.name && (
                        <p className="text-muted-foreground text-xs font-mono">
                            Uploaded by: {candidate.user.name}
                        </p>
                    )}
                    <p className="text-acid text-sm font-mono">
                        {candidate.user?.email}
                    </p>
                    <p className="text-muted-foreground text-xs font-mono capitalize">
                        {candidate.primary_sector || 'Unknown sector'}
                    </p>
                </div>
                <Badge className={`font-mono text-xs ${getFitLabelStyle(candidate.fit_label)}`}>
                    {candidate.fit_label}
                </Badge>
            </div>

            {/* Match Reason */}
            {candidate.match_reason && (
                <p className="text-neon-cyan/80 text-xs italic mb-3 leading-relaxed">
                    {candidate.match_reason}
                </p>
            )}

            {/* Matched Terms */}
            {candidate.matched_terms?.length > 0 && (
                <div className="mb-3">
                    <p className="text-muted-foreground text-xs font-mono uppercase tracking-wider mb-1">Matched:</p>
                    <div className="flex flex-wrap gap-1">
                        {candidate.matched_terms.slice(0, 6).map((term, idx) => (
                            <Badge 
                                key={idx}
                                variant="outline"
                                className="font-mono text-xs border-acid/30 text-acid bg-acid/5"
                            >
                                {term}
                            </Badge>
                        ))}
                    </div>
                </div>
            )}

            {/* Skills */}
            {candidate.skills?.length > 0 && (
                <div className="flex flex-wrap gap-1">
                    {candidate.skills.slice(0, 5).map((skill, idx) => (
                        <Badge 
                            key={idx}
                            variant="outline"
                            className="font-mono text-[10px] border-border text-muted-foreground"
                        >
                            {skill}
                        </Badge>
                    ))}
                    {candidate.skills.length > 5 && (
                        <Badge variant="outline" className="font-mono text-[10px] border-border text-muted-foreground">
                            +{candidate.skills.length - 5} more
                        </Badge>
                    )}
                </div>
            )}

            {/* Contact Links */}
            <div className="flex gap-2 mt-3 pt-3 border-t border-border">
                {candidate.user?.email && (
                    <a 
                        href={`mailto:${candidate.user.email}`}
                        className="text-muted-foreground hover:text-acid transition-colors"
                        title={candidate.user.email}
                    >
                        <Mail className="w-4 h-4" strokeWidth={1.5} />
                    </a>
                )}
                {candidate.user?.github_url && (
                    <a 
                        href={candidate.user.github_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-muted-foreground hover:text-acid transition-colors"
                    >
                        <Github className="w-4 h-4" strokeWidth={1.5} />
                    </a>
                )}
                {candidate.user?.linkedin_url && (
                    <a 
                        href={candidate.user.linkedin_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-muted-foreground hover:text-acid transition-colors"
                    >
                        <Linkedin className="w-4 h-4" strokeWidth={1.5} />
                    </a>
                )}
                {candidate.user?.telegram_handle && (
                    <a 
                        href={`https://t.me/${candidate.user.telegram_handle}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-muted-foreground hover:text-[#0088cc] transition-colors"
                        title={`@${candidate.user.telegram_handle}`}
                    >
                        <Send className="w-4 h-4" strokeWidth={1.5} />
                    </a>
                )}
            </div>
        </div>
    );
}
